// Small shared pieces of the deliverable screens: the status badge, the
// "[ source ]" button, and the table shell every list uses (loading, error,
// empty, rows with data-row-id).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger, SpecraBadge, SpecraButton, SpecraDataTable, SpecraLink, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowRight, ChevronRight, ExternalLink, FileText, Link2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useArtifactNames } from '../../../../hooks/useDeliveryDocuments';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { DocumentPreviewDialog } from '../../_shared/components/DocumentPreviewDialog';
import { EmptyState, ErrorState } from '../../_shared/components/PageStates';
import { useProgramDocuments } from '../../Docs/hooks';
import { useCmDrawerParams } from '../drawerParams';
import { useAwaitingApproval } from '../hooks';
import { dateClinDisplay, dutyClinDisplay } from '../lib/dutyClin';
import { pwsChipLabel, sourceDocChipLabel } from '../lib/dutySource';
import { cmDate } from '../lib/format';
import { decisionMark, occurrenceStatus, SOW_STATE } from '../lib/status';
/** Short label for a URL when the proposer left none (SAD-917). */ export function linkLabelFromUrl(url) {
    try {
        const u = new URL(url);
        const leaf = u.pathname.split('/').filter(Boolean).pop();
        return decodeURIComponent(leaf || u.hostname || url);
    } catch  {
        return url;
    }
}
/** Display name for a proposed attachment: real file name or link, never "Evidence". */ export function attachmentDisplayName(a, names) {
    if (a.url) {
        if (a.label && a.label !== 'Evidence') return a.label;
        return linkLabelFromUrl(a.url);
    }
    const resolved = a.artifactId ? names?.get(a.artifactId) : undefined;
    if (resolved) return resolved;
    if (a.label && a.label !== 'Evidence') return a.label;
    return 'File';
}
/** Compact CLIN next to a PWS ref / on a date row (SAD-894). */ export function DutyClinLabel({ display, testId, tone = 'muted' }) {
    if (!display.label) return null;
    const text = /*#__PURE__*/ _jsx(SpecraText, {
        as: "span",
        size: "caption",
        tone: tone,
        className: "tabular-nums",
        "data-testid": testId,
        children: display.label
    });
    if (!display.tooltip) return text;
    return /*#__PURE__*/ _jsx(SpecraTooltip, {
        content: display.tooltip,
        children: /*#__PURE__*/ _jsx("span", {
            className: "inline-flex",
            children: text
        })
    });
}
/**
 * SAD-900: PWS + CLIN + source-document chips for a deliverable row or header.
 * The document chip opens a preview when the file is on the program.
 */ export function DutySourceChips({ programId, origin, pwsRef, letter, dutyKey, sourceDocumentId, clins, clinDisplay, testIds }) {
    const docs = useProgramDocuments(programId);
    const [preview, setPreview] = useState(false);
    const clin = clinDisplay ?? dutyClinDisplay(dutyKey, clins);
    const pws = pwsChipLabel(pwsRef, letter);
    const doc = sourceDocumentId ? docs.data?.items.find((d)=>d.id === sourceDocumentId) : undefined;
    const docLabel = sourceDocChipLabel(doc ? {
        id: doc.id,
        title: doc.title,
        fileName: doc.fileName,
        category: doc.category
    } : sourceDocumentId ? {
        id: sourceDocumentId
    } : undefined, origin);
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-center gap-1.5",
        children: [
            pws ? /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                "data-testid": testIds?.pws,
                children: pws
            }) : null,
            clin.label ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: clin.tooltip ?? clin.label,
                children: /*#__PURE__*/ _jsx("span", {
                    className: "inline-flex",
                    children: /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "info",
                        className: "tabular-nums",
                        "data-testid": testIds?.clin,
                        children: clin.label
                    })
                })
            }) : null,
            /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "outline",
                "data-testid": testIds?.sourceDoc,
                className: doc?.fileId ? 'cursor-pointer' : undefined,
                onClick: doc?.fileId ? (e)=>{
                    e.stopPropagation();
                    setPreview(true);
                } : undefined,
                role: doc?.fileId ? 'button' : undefined,
                children: docLabel
            }),
            doc?.fileId ? /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: doc.fileId,
                title: docLabel,
                open: preview,
                onOpenChange: setPreview
            }) : null
        ]
    });
}
/** Rectangular tinted status badge (layout spec: never a pill or a dot). */ export function CmStatusBadge({ status, testId }) {
    const plain = /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: status.tone,
        "data-testid": testId,
        "data-state": status.state,
        children: status.label
    });
    const badge = status.tooltip ? /*#__PURE__*/ _jsx(SpecraTooltip, {
        content: status.tooltip,
        children: /*#__PURE__*/ _jsx("span", {
            children: plain
        })
    }) : plain;
    return status.href ? /*#__PURE__*/ _jsx(SpecraLink, {
        href: status.href,
        renderLink: renderRouterLink,
        onClick: (e)=>e.stopPropagation(),
        children: badge
    }) : badge;
}
export function SowStateBadge({ state }) {
    const s = SOW_STATE[state] ?? {
        label: state || 'Unknown',
        tone: 'neutral'
    };
    return /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: s.tone,
        "data-testid": Testids.cmWorkstream.sowState,
        "data-state": state,
        children: s.label
    });
}
/**
 * The open proposal beside a date's state, by its step ("Waiver proposed",
 * "COR agreed: awaiting approval"); clicking opens the date. A proposed
 * waiver leaves the state itself alone: the date is still owed (SAD-866).
 */ export function CmAwaitingApproval({ programId, occurrenceId }) {
    const { byOccurrence } = useAwaitingApproval(programId);
    const drawer = useCmDrawerParams();
    const decision = byOccurrence.get(occurrenceId);
    if (!decision) return null;
    const mark = decisionMark(decision);
    return /*#__PURE__*/ _jsx(SpecraButton, {
        size: "xs",
        variant: "ghost",
        className: "h-auto p-0",
        "aria-label": `${mark}: open the date`,
        onClick: (e)=>{
            e.stopPropagation();
            drawer.open('occ', occurrenceId);
        },
        "data-testid": Testids.cmApprovals.mark,
        "data-row-id": occurrenceId,
        "data-step": decision.step,
        children: /*#__PURE__*/ _jsx(SpecraBadge, {
            variant: "warning",
            children: mark
        })
    });
}
/**
 * SAD-991: an approved date stays approved when a later filing comes in; the
 * filing still awaiting review shows beside the state, and clicking opens
 * its review.
 */ export function CmPendingSubmission({ occ }) {
    const drawer = useCmDrawerParams();
    const submissionId = occ.status?.pendingSubmissionId;
    if (!submissionId) return null;
    return /*#__PURE__*/ _jsx(SpecraButton, {
        size: "xs",
        variant: "ghost",
        className: "h-auto p-0",
        "aria-label": "New submission awaiting approval: review it",
        onClick: (e)=>{
            e.stopPropagation();
            drawer.open('review', submissionId, {
                occ: occ.id
            });
        },
        "data-testid": Testids.cmOccurrence.pendingSubmission,
        "data-row-id": occ.id,
        children: /*#__PURE__*/ _jsx(SpecraBadge, {
            variant: "warning",
            children: "New submission awaiting approval"
        })
    });
}
/**
 * "[ source ]": where a term or rule came from. The popover names the
 * locator and quotes the sentence; with neither, nothing renders — an empty
 * provenance button is a promise the record cannot keep.
 */ export function CmSourceButton({ locator, quote, label = 'source' }) {
    if (!locator && !quote) return null;
    return /*#__PURE__*/ _jsxs(Popover, {
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsxs(SpecraButton, {
                    size: "xs",
                    variant: "outline",
                    className: "shrink-0 text-muted-foreground hover:text-foreground",
                    "data-testid": Testids.cmWorkstream.sourceButton,
                    "aria-label": `Show ${label}${locator ? `: ${locator}` : ''}`,
                    children: [
                        "[ ",
                        label,
                        " ]"
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PopoverContent, {
                className: "w-80 space-y-1.5 text-[length:var(--fs-ui-body)]",
                "data-testid": Testids.cmWorkstream.sourcePopover,
                children: [
                    locator ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: locator
                    }) : null,
                    quote ? /*#__PURE__*/ _jsxs("blockquote", {
                        className: "border-l-2 border-border pl-2 italic",
                        children: [
                            "“",
                            quote,
                            "”"
                        ]
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: "No quote recorded."
                    })
                ]
            })
        ]
    });
}
/** SpecraDataTable plus the page states; rows carry data-row-id for ui-cli. */ export function CmTable({ rows, columns, rowKey, testId, rowTestId, onRowClick, rowChevron, getRowLabel, isLoading, error, onRetry, empty, caption, rowActions, sortColumn, sortDirection, onSortChange, pagination, tableProps, tableRef }) {
    if (error) return /*#__PURE__*/ _jsx(ErrorState, {
        error: error,
        title: "Could not load",
        onRetry: onRetry
    });
    if (!isLoading && rows.length === 0) return /*#__PURE__*/ _jsx(_Fragment, {
        children: empty ?? /*#__PURE__*/ _jsx(EmptyState, {
            title: "Nothing here yet"
        })
    });
    return /*#__PURE__*/ _jsx(SpecraDataTable, {
        caption: caption,
        columns: rowChevron ? columns.map((c)=>c.id === 'title' && c.cell ? {
                ...c,
                cell: (...a)=>/*#__PURE__*/ _jsx("div", {
                        className: "underline-offset-2 group-hover/row:underline",
                        children: c.cell?.(...a)
                    })
            } : c) : columns,
        rows: rows,
        getRowId: rowKey,
        loading: isLoading,
        testId: testId,
        rowTestId: rowTestId,
        onRowClick: onRowClick,
        getRowLabel: getRowLabel,
        rowActions: rowChevron && onRowClick ? (row)=>/*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-end gap-2",
                children: [
                    rowActions?.(row),
                    /*#__PURE__*/ _jsx(ChevronRight, {
                        "aria-hidden": "true",
                        className: "size-4 text-muted-foreground/60 transition-colors group-hover/row:text-foreground",
                        onClick: ()=>onRowClick(row)
                    })
                ]
            }) : rowActions,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        onSortChange: onSortChange,
        pagination: pagination,
        tableProps: tableProps,
        tableRef: tableRef,
        getRowProps: (row)=>({
                'data-row-id': rowKey(row),
                className: rowChevron ? 'group/row' : undefined
            })
    });
}
/**
 * The dates a `?state=` link lists (a KPI tile's rows), on the Deliverables
 * list and a team page alike. A row opens its date drawer.
 */ // specra-allow local-composite: a column set handed to CmTable (SpecraDataTable), no restyle
export function CmStateTable({ programId, occurrences, dutyTitle, finalTierOf, teamName, onOpen, isLoading, empty }) {
    const columns = [
        {
            id: 'deliverable',
            header: 'Deliverable',
            cell: (o)=>dutyTitle(o.dutyId) ?? 'Deliverable'
        },
        {
            id: 'team',
            header: 'Team',
            cell: (o)=>teamName(o.workstreamId)
        },
        {
            id: 'due',
            header: 'Due',
            align: 'right',
            cell: (o)=>cmDate(o.dueOn)
        },
        {
            id: 'state',
            header: 'State',
            cell: (o)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex flex-wrap items-center gap-1",
                    children: [
                        /*#__PURE__*/ _jsx(CmStatusBadge, {
                            status: occurrenceStatus(o, finalTierOf(o.dutyId))
                        }),
                        /*#__PURE__*/ _jsx(CmAwaitingApproval, {
                            programId: programId,
                            occurrenceId: o.id
                        })
                    ]
                })
        }
    ];
    return /*#__PURE__*/ _jsx(CmTable, {
        caption: "Dates in this state",
        rows: occurrences,
        columns: columns,
        rowKey: (o)=>o.id,
        rowTestId: Testids.cmDuties.stateRow,
        isLoading: isLoading,
        onRowClick: (o)=>onOpen(o.id),
        getRowLabel: (o)=>`${dutyTitle(o.dutyId) ?? 'Deliverable'} due ${cmDate(o.dueOn)}`,
        empty: empty
    });
}
/**
 * A proposal's evidence (a program document): its file name and Open, which
 * previews the file. Renders nothing when there is none.
 */ export function CmEvidence({ programId, documentId, testId }) {
    const docs = useProgramDocuments(documentId ? programId : null);
    const [open, setOpen] = useState(false);
    if (!documentId) return null;
    const doc = docs.data?.items.find((x)=>x.id === documentId);
    const name = doc?.fileName || doc?.title || 'Evidence';
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex items-center gap-1.5",
        "data-testid": testId,
        children: [
            /*#__PURE__*/ _jsx(FileText, {
                className: "h-3.5 w-3.5 shrink-0 text-muted-foreground",
                "aria-hidden": true
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "truncate",
                children: name
            }),
            doc?.fileId ? /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "link",
                size: "inline",
                onClick: (e)=>{
                    e.stopPropagation();
                    setOpen(true);
                },
                children: "Open"
            }) : null,
            doc?.fileId ? /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: doc.fileId,
                title: name,
                open: open,
                onOpenChange: setOpen
            }) : null
        ]
    });
}
/**
 * Evidence on a proposal: a program document (waiver/extension) and/or the
 * files and links on a submission_edit / submission_evidence (SAD-902).
 * Returns null when there is nothing to show (caller may render "—").
 */ export function CmDecisionEvidence({ programId, evidenceDocumentId, attachments, testId, artifactNames }) {
    const atts = attachments ?? [];
    const artifactIds = useMemo(()=>artifactNames ? [] : atts.map((a)=>a.artifactId).filter(Boolean), [
        atts,
        artifactNames
    ]);
    const own = useArtifactNames(artifactIds);
    const names = artifactNames ?? own;
    const [preview, setPreview] = useState(null);
    if (!evidenceDocumentId && atts.length === 0) return null;
    return /*#__PURE__*/ _jsxs("span", {
        className: "flex flex-col gap-1",
        "data-testid": testId,
        children: [
            evidenceDocumentId ? /*#__PURE__*/ _jsx(CmEvidence, {
                programId: programId,
                documentId: evidenceDocumentId,
                testId: `${testId}-doc`
            }) : null,
            atts.map((a, i)=>{
                const name = attachmentDisplayName(a, names);
                const key = `${a.artifactId || a.url || name}-${i}`;
                if (a.url) {
                    return /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex items-center gap-1.5 min-w-0",
                        children: [
                            /*#__PURE__*/ _jsx(Link2, {
                                className: "h-3.5 w-3.5 shrink-0 text-muted-foreground",
                                "aria-hidden": true
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate",
                                children: name
                            }),
                            /*#__PURE__*/ _jsxs(SpecraLink, {
                                href: a.url,
                                target: "_blank",
                                rel: "noreferrer noopener",
                                className: "inline-flex shrink-0 items-center gap-1",
                                onClick: (e)=>e.stopPropagation(),
                                children: [
                                    "Open ",
                                    /*#__PURE__*/ _jsx(ExternalLink, {
                                        className: "h-3 w-3",
                                        "aria-hidden": true
                                    })
                                ]
                            })
                        ]
                    }, key);
                }
                return /*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-1.5 min-w-0",
                    children: [
                        /*#__PURE__*/ _jsx(FileText, {
                            className: "h-3.5 w-3.5 shrink-0 text-muted-foreground",
                            "aria-hidden": true
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "truncate",
                            children: name
                        }),
                        a.artifactId ? /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "link",
                            size: "inline",
                            onClick: (e)=>{
                                e.stopPropagation();
                                setPreview({
                                    artifactId: a.artifactId,
                                    name
                                });
                            },
                            children: "Open"
                        }) : null
                    ]
                }, key);
            }),
            preview ? /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: preview.artifactId,
                title: preview.name,
                open: true,
                onOpenChange: (open)=>{
                    if (!open) setPreview(null);
                }
            }) : null
        ]
    });
}
/**
 * SAD-969: "SOW doesn't mention this · answer" on a deliverable row a SOW
 * never mentions: owed per the award schedule until answered, one click to
 * the open question.
 */ export function SowSilentLink({ href, testId }) {
    return /*#__PURE__*/ _jsx(SpecraLink, {
        href: href,
        renderLink: renderRouterLink,
        className: "whitespace-nowrap text-[length:var(--fs-ui-meta)]",
        onClick: (e)=>e.stopPropagation(),
        "data-testid": testId,
        title: "Until answered, this deliverable is owed per the award schedule",
        children: "SOW doesn’t mention this · answer"
    });
}
/** SAD-968: a link to another team's page, naming the routing. */ export function routedLink(programId, teamId, label, testId) {
    return /*#__PURE__*/ _jsxs(SpecraLink, {
        href: `/delivery/programs/${programId}/workstreams/${teamId}`,
        renderLink: renderRouterLink,
        className: "inline-flex items-center gap-1",
        onClick: (e)=>e.stopPropagation(),
        "data-testid": testId,
        "data-team-id": teamId,
        children: [
            label,
            " ",
            /*#__PURE__*/ _jsx(ArrowRight, {
                className: "h-3 w-3",
                "aria-hidden": true
            })
        ]
    }, teamId);
}
/** The CLIN a date falls under: the deliverable's own, else its period's line for a family, else none. */ export function occurrenceClinDisplay(dutyKey, periodId, lk, clins) {
    return dateClinDisplay({
        dutyKey,
        optionNumber: lk.periodById.get(periodId)?.optionNumber,
        clins
    });
}
/** "#2 3 Oct (file + link)", or a dash when nothing was filed. */ export function CmLatestSubmission({ occ }) {
    return occ.latestSubmission ? /*#__PURE__*/ _jsxs("span", {
        children: [
            "#",
            occ.latestSubmission.attempt,
            " ",
            cmDate(occ.latestSubmission.submittedOn, ''),
            /*#__PURE__*/ _jsxs("span", {
                className: "text-muted-foreground",
                children: [
                    ' ',
                    "(",
                    occ.latestSubmission.attachments.map((a)=>a.url ? 'link' : 'file').join(' + '),
                    ")"
                ]
            })
        ]
    }) : /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
}
/** A date's status: badge, awaiting-approval note and pending-submission note. */ export function CmOccurrenceStatus({ programId, occ, finalReviewTier }) {
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-center gap-1",
        children: [
            /*#__PURE__*/ _jsx(CmStatusBadge, {
                status: occurrenceStatus(occ, finalReviewTier)
            }),
            /*#__PURE__*/ _jsx(CmAwaitingApproval, {
                programId: programId,
                occurrenceId: occ.id
            }),
            /*#__PURE__*/ _jsx(CmPendingSubmission, {
                occ: occ
            })
        ]
    });
}
